/**
 * Fetch-and-zip core, shared by the two browser-side downloaders.
 *
 * Both the artwork downloader (`features/browserDownload/`) and the bulk mockup downloader
 * (`features/mockupsAll/`) do the same physical work: fetch a batch of URLs with bounded
 * concurrency, stream them into a zip as they arrive, hand the zip to the OS. Only the
 * planning and the bookkeeping differ. This module is the part that is genuinely identical,
 * factored out rather than copied — the retry rules, the credential rule and the streaming
 * discipline below were each arrived at by fixing a specific bug, and a second copy would
 * quietly not have those fixes.
 *
 * Deliberately no DOM dependencies beyond the anchor click, so this can move into a Web
 * Worker without restructuring — background tabs throttle timers hard, and these downloads
 * run for tens of minutes, sometimes hours.
 */

/** Parallel fetches. Measured against `/mockup-render` on 2026-09-02: latency stays flat at
 *  ~0.85 s from 1 to 10 concurrent (1.2 → 9.8 requests/s), so 10 extracts nearly all the
 *  available throughput without hammering one origin. Re-measured 2026-09-06 against
 *  production: 0.86–1.07 s per render, six concurrent in 1.34 s. Still the right number. */
export const CONCURRENCY = 10;

/** Retries per file. A mockup render is a Lambda invocation and can cold-start; a single
 *  blip should not cost the reseller a whole zip. */
const MAX_ATTEMPTS = 3;

/** Grace period before releasing the LAST zip, which has no successor to trigger it. */
export const FINAL_ZIP_RELEASE_MS = 30_000;

export interface DownloadFile {
  /** The artwork this file belongs to. 0 means "not an artwork" (the metadata CSV). */
  artworkId: number;
  /** Name inside the zip. */
  target: string;
  url: string;
  /**
   * Literal file content, for a file the server already holds — currently an artist's
   * `biography.txt`.
   *
   * ⚠️ Present means NO network request is made and `url` is ignored. The alternative was a
   * session-authenticated endpoint per artist, mirroring `/csv-dl`; a biography is under a
   * kilobyte and the server has it in hand when it plans the part, so that would have been
   * one round trip each to fetch something already sent.
   */
  content?: string;
}

/** What one batch's pass produced, filled in as the generator runs. */
export interface FetchTally {
  /** Files fetched so far, successes and failures alike. */
  done: number;
  /** Names of files that could not be had after every retry. */
  failures: string[];
  /** Artworks the caller's `isDelivered` predicate accepted. */
  delivered: Set<number>;
  /** Files actually written into the zip. */
  zipped: number;
}

export function emptyTally(): FetchTally {
  return { done: 0, failures: [], delivered: new Set(), zipped: 0 };
}

/** Fetch one file, retrying transient failures. Returns null when it cannot be had. */
async function fetchFile(file: DownloadFile, signal: AbortSignal): Promise<Blob | null> {
  // Content the server already sent: no request, no retries, no failure mode. Checked
  // before the loop so an inline file cannot be counted as a network failure.
  if (file.content !== undefined) {
    return new Blob([file.content], { type: "text/plain;charset=utf-8" });
  }
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      // Credentials per origin, and this matters twice over.
      //
      // Our own endpoints (`/mockup-render`, `/csv-dl`) are session-authenticated and need
      // the cookie. S3 presigned URLs carry their own signature and must NOT get one: a
      // credentialed cross-origin request requires the bucket to echo an explicit origin AND
      // `Access-Control-Allow-Credentials`, which a wildcard CORS policy cannot do — so
      // sending cookies would fail even against a correctly configured bucket.
      const sameOrigin = file.url.startsWith("/") || file.url.startsWith(location.origin);
      const res = await fetch(file.url, {
        signal,
        credentials: sameOrigin ? "same-origin" : "omit",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.blob();
    } catch {
      // The error itself carries nothing actionable — every failure mode here (network blip,
      // cold-start timeout, 5xx from a render) is handled the same way: retry, then give up
      // and let the caller record the filename. An abort is the user's decision, not a
      // failure, so it stops immediately rather than retrying.
      if (signal.aborted) return null;
      if (attempt === MAX_ATTEMPTS) return null;
      // Linear backoff; a cold Lambda needs a moment, not a thundering herd.
      await new Promise((r) => setTimeout(r, 500 * attempt));
    }
  }
  return null;
}

/**
 * Fetch a batch's files with bounded look-ahead, yielding them IN ORDER for `client-zip` to
 * consume as it writes the archive.
 *
 * This is what keeps memory flat on large jobs. An earlier version fetched the whole batch
 * into an array of blobs and only then built the zip, so peak usage was the batch twice over
 * — every input still referenced while the entire output was assembled. Streaming means a
 * blob is dropped as soon as it has been written, and at most CONCURRENCY of them are alive
 * at once, whatever the batch size.
 *
 * Order is preserved because the window is drained FIFO by index: a reseller comparing two
 * downloads sees the same layout, and it costs nothing — a slow file simply holds its slot
 * while the ones behind it are already in flight.
 *
 * `isDelivered` says which files count as delivering their artwork. The artwork downloader
 * passes "the original landed", because a failed mockup must not withhold an artwork whose
 * licence the original carries. The mockup downloader has no such file and passes nothing.
 */
export async function* prefetchFiles(
  files: DownloadFile[],
  signal: AbortSignal,
  tally: FetchTally,
  onFile: () => void,
  isDelivered?: (file: DownloadFile) => boolean
): AsyncGenerator<{ name: string; input: Blob }> {
  const window: { file: DownloadFile; blob: Promise<Blob | null> }[] = [];
  let next = 0;

  const fill = (): void => {
    while (window.length < CONCURRENCY && next < files.length) {
      const file = files[next++]!;
      window.push({ file, blob: fetchFile(file, signal) });
    }
  };
  fill();

  while (window.length > 0) {
    const entry = window.shift()!;
    const blob = await entry.blob;
    tally.done++;
    onFile();
    // Top the window back up only after one has been consumed, so look-ahead stays bounded.
    fill();
    if (signal.aborted) return;

    if (!blob) {
      tally.failures.push(entry.file.target);
      continue;
    }
    if (isDelivered?.(entry.file)) {
      tally.delivered.add(entry.file.artworkId);
    }
    tally.zipped++;
    yield { name: entry.file.target, input: blob };
  }
}

/**
 * Hand a finished zip to the browser, returning its object URL so the caller can release it.
 *
 * Revoking is the caller's job because the safe moment is when the NEXT zip is handed over:
 * revoking synchronously can cancel a download that has not started reading yet, while a
 * fixed timer meant several multi-hundred-MB zips could be retained at once on a job whose
 * batches finish quickly. Releasing the previous one gives it a whole batch's grace and
 * still caps retention at a single archive.
 */
export function saveZip(blob: Blob, filename: string): string {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return url;
}
